import type { InfiniteData, QueryFunctionContext } from '@tanstack/react-query';
import { infiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import type { PaginationParams, SortingParams } from 'lib/api';
import { apiClient } from 'lib/api';
import type { FacebookCreativeAdByRegionDTO } from './types';
import type { AdType } from '../types';
import { parseAdsLibraryApiPath } from './parseAdsLibraryApiPath';

interface FilteringFields {
  network: string;
  region: string;
  company_id: number[];
  branch_id: number[];
  page_id: number[];
  domain: string[];
  flow: string[];
  ad_creative_type: AdType[];
  language: string[];
  date_from: string;
  date_to: string;
  running: boolean;
  new_only: boolean;
  include_welltech: boolean;
}

export interface ListResponse<T> {
  data: T[];
  meta: {
    count: number;
    limit: number;
    offset: number;
    load_ts: string;
  };
}

type GetCreativesOptions = PaginationParams & Partial<SortingParams> & Partial<FilteringFields>;

type RequestParams = Omit<GetCreativesOptions, 'network' | 'region' | 'offset'>;

const composeRequestParams = ({
  network,
  region,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  offset,
  running,
  new_only,
  include_welltech,
  ...restParams
}: GetCreativesOptions): RequestParams => {
  return {
    ...restParams,
    ...(running && { running }),
    ...(region === 'us' && new_only !== undefined && { new_only }),
    ...(network === 'facebook' && include_welltech !== undefined && { include_welltech }),
  };
};

export const getFBCreatives = async ({
  signal,
  queryKey,
  pageParam,
}: QueryFunctionContext<[string, string, RequestParams], number>): Promise<ListResponse<FacebookCreativeAdByRegionDTO>> => {
  const res = await apiClient.get<ListResponse<FacebookCreativeAdByRegionDTO>>(queryKey[1], {
    params: { ...queryKey[2], offset: pageParam },
    signal,
  });
  return res.data;
};

export interface FBCreativesData {
  pages: FacebookCreativeAdByRegionDTO[][];
  count: number;
  loadTs?: string;
}

export const getFBCreativesOptions = (params: GetCreativesOptions) =>
  infiniteQueryOptions<
    ListResponse<FacebookCreativeAdByRegionDTO>,
    Error,
    FBCreativesData,
    [string, string, RequestParams],
    number
  >({
    staleTime: 60 * 60 * 1000,
    queryFn: getFBCreatives,
    queryKey: [
      'fb-ads-creatives',
      parseAdsLibraryApiPath('ad_creatives', { network: params.network, region: params.region }),
      composeRequestParams(params),
    ],
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.data.length) {
        return null;
      }
      const nextOffset = lastPageParam + lastPage.meta.limit;
      return nextOffset < lastPage.meta.count ? nextOffset : null;
    },
    select: (data: InfiniteData<ListResponse<FacebookCreativeAdByRegionDTO>>) => ({
      count: data.pages[0]?.meta.count ?? 0,
      loadTs: data.pages[0]?.meta.load_ts,
      pages: data.pages.map((page) => page.data),
    }),
  });

export const useFBCreatives = (params: GetCreativesOptions) =>
  useSuspenseInfiniteQuery(getFBCreativesOptions(params));
